import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type MenuOption } from '@saviynt/design-system';

import {
  RENDER_MODES,
  VIOLATIONS_TAB_LIST_INITIAL_FILTERS,
} from '../../../shared/constants';
import { detailsActions, globalActions } from '../../../shared/store/actions';
import {
  type ActiveFiltersState,
  type ActiveGraphPointState,
  type ActiveWidgetItem,
  type AdvancedFilterState,
  type InsightItemTypes,
  type TabFilterState,
  type ViolationsActionManagerState,
  type WidgetMenuItem,
} from '../../../shared/types';
import {
  type ActionInfoState,
  type ColumnState,
  type RenderModeState,
  type SortState,
  type ViolationsTabSidePanelGroupRowContextState,
  type ViolationsTabSidePanelRowContextState,
  type ViolationsTabState,
  ViolationsTabType,
} from '../data-layer/violationsTabTypes';
import { type FilterCacheState } from '../hooks';

const initialState: ViolationsTabState = {
  isLoading: false,
  controls: {
    renderMode: RENDER_MODES.GRAPH,
    filters: VIOLATIONS_TAB_LIST_INITIAL_FILTERS,
    isInsightsOpen: true,
    tabFilter: [] as TabFilterState[],
    tabInAction: null,
    activeFilters: [] as ActiveFiltersState[],
    isFilterBarOpen: false,
    searchKey: null,
    sortInAction: null,
    activeColumns: [] as ColumnState[],
    filterCache: {
      entries: {},
      ids: {},
      metadata: {},
    } as FilterCacheState,
    activeInsight: null,
    activeGraphPoint: null,
    activeRowContext: null,
    widgets: [] as WidgetMenuItem[],
    activeWidgetItem: null,
  },
  list: {
    isListLoading: false,
    totalCount: null,
    rowIdToTransition: null,
    isRefresh: false,
  },
  bulkaction: {
    isFooterVisible: false,
    bulkCount: 0,
    isBulkChecked: false,
    isClear: false,
    sodKeys: {
      in: [],
      notIn: [],
    },
    actionInfo: [],
  },
  sidepanel: {
    isSidePanelVisible: false,
    rowContext: { current: { id: '' } },
  },
  actionManager: null,
};

const violationsTabSlice = createSlice({
  name: ViolationsTabType.SLICE_NAME,
  initialState,
  reducers: {
    // General section
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    // Controls section
    setRenderMode: (state, action: PayloadAction<RenderModeState>) => {
      const targetRenderMode = action.payload;

      if (targetRenderMode === state.controls.renderMode) {
        return;
      }

      state.controls.renderMode = targetRenderMode;

      // When mode changed, close sidepanel if open
      if (state.sidepanel.isSidePanelVisible) {
        state.sidepanel.isSidePanelVisible = false;
      }

      // When moving to graph mode, clear bulk utilities
      if (targetRenderMode === RENDER_MODES.GRAPH) {
        state.list.totalCount = null;
        state.bulkaction.isBulkChecked = false;
        state.bulkaction.isFooterVisible = false;

        // When moving to graph, clear widget
        if (state.controls.activeWidgetItem !== null) {
          state.controls.activeWidgetItem = null;
        }
      }

      // When moving to list, set loading to list
      if (targetRenderMode === RENDER_MODES.LIST) {
        state.list.isListLoading = true;
      }
    },
    setIsInsightsOpen: (state, action: PayloadAction<boolean>) => {
      state.controls.isInsightsOpen = action.payload;

      if (action.payload) {
        state.sidepanel.isSidePanelVisible = false;
      }
    },
    setTabFilters: (state, action: PayloadAction<TabFilterState[]>) => {
      state.controls.tabFilter = action.payload;
    },
    setTabInAction: (state, action: PayloadAction<string | null>) => {
      state.controls.tabInAction = action.payload;

      if (state.sidepanel.isSidePanelVisible) {
        state.sidepanel.isSidePanelVisible = false;
      }
    },
    setAdvancedFilters: (
      state,
      action: PayloadAction<AdvancedFilterState[]>
    ) => {
      state.controls.filters = action.payload;
    },
    setActiveFilters: (state, action: PayloadAction<ActiveFiltersState[]>) => {
      state.controls.activeFilters = action.payload;
    },
    setIsFilterBarOpen: (state, action: PayloadAction<boolean>) => {
      state.controls.isFilterBarOpen = action.payload;
    },
    setFiltersCache: (state, action: PayloadAction<FilterCacheState>) => {
      state.controls.filterCache = action.payload;
    },
    hydrateFilterOptions: (
      state,
      action: PayloadAction<{ filterKey: string; options: MenuOption[] }>
    ) => {
      const { filterKey, options } = action.payload;

      state.controls.filters = state.controls.filters.map((filter) => {
        if (filter.value === filterKey) {
          return {
            ...filter,
            options,
          };
        }

        return filter;
      });
    },
    setSearchKey: (state, action: PayloadAction<string | null>) => {
      state.controls.searchKey = action.payload;
    },
    setSortInAction: (state, action: PayloadAction<SortState>) => {
      state.controls.sortInAction = action.payload;
    },
    setViewColumns: (state, action: PayloadAction<ColumnState[]>) => {
      state.controls.activeColumns = action.payload;
    },
    setActiveInsight: (
      state,
      action: PayloadAction<InsightItemTypes | null>
    ) => {
      state.controls.activeInsight = action.payload;
    },
    setActiveGraphPoint: (
      state,
      action: PayloadAction<ActiveGraphPointState | null>
    ) => {
      state.controls.activeGraphPoint = action.payload;
    },
    setGraphRowContext: (
      state,
      action: PayloadAction<ViolationsTabSidePanelRowContextState | null>
    ) => {
      state.controls.activeRowContext = action.payload;
    },
    setWidgets: (state, action: PayloadAction<WidgetMenuItem[]>) => {
      state.controls.widgets = action.payload;
    },
    setActiveWidgetItem: (
      state,
      action: PayloadAction<ActiveWidgetItem | null>
    ) => {
      state.controls.activeWidgetItem = action.payload;
    },
    // List operations
    setIsListLoading: (state, action: PayloadAction<boolean>) => {
      state.list.isListLoading = action.payload;
    },
    setListTotalCount: (state, action: PayloadAction<number | null>) => {
      state.list.totalCount = action.payload;
    },
    setListTransitionId: (
      state,
      action: PayloadAction<number | string | null>
    ) => {
      state.list.rowIdToTransition = action.payload;
    },
    setListIsRefresh: (state, action: PayloadAction<boolean>) => {
      state.list.isRefresh = action.payload;
    },
    // Bulk actions section
    setBulkCount: (state, action: PayloadAction<number>) => {
      state.bulkaction.bulkCount = action.payload;
    },
    setIsBulkChecked: (state, action: PayloadAction<boolean>) => {
      state.bulkaction.isBulkChecked = action.payload;
      state.bulkaction.isFooterVisible = action.payload;
      state.bulkaction.sodKeys = { in: [], notIn: [] };
      state.bulkaction.actionInfo = [];

      if (action.payload) {
        state.controls.isInsightsOpen = false;
        state.bulkaction.bulkCount = state.list.totalCount ?? 0;
      } else {
        state.bulkaction.bulkCount = 0;
      }
    },
    setIsFooterVisible: (state, action: PayloadAction<boolean>) => {
      state.bulkaction.isFooterVisible = action.payload;
    },
    setBulkSodKeys: (
      state,
      action: PayloadAction<{ in: string[]; notIn: string[] }>
    ) => {
      state.bulkaction.sodKeys = action.payload;
    },
    setBulkIsClear: (state, action: PayloadAction<boolean>) => {
      state.bulkaction.isClear = action.payload;
    },
    setBulkActionInfo: (state, action: PayloadAction<ActionInfoState[]>) => {
      state.bulkaction.actionInfo = action.payload;
    },
    // Sidepanel section
    setIsSidePanelVisible: (state, action: PayloadAction<boolean>) => {
      state.sidepanel.isSidePanelVisible = action.payload;

      if (action.payload) {
        state.controls.isInsightsOpen = false;
      } else {
        state.sidepanel.rowContext = { current: { id: '' } };
      }
    },
    setRowContext: (
      state,
      action: PayloadAction<ViolationsTabSidePanelGroupRowContextState>
    ) => {
      state.sidepanel.rowContext = action.payload;
    },
    // Actions
    setViolationsActionManager: (
      state,
      action: PayloadAction<ViolationsActionManagerState | null>
    ) => {
      state.actionManager = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Add extra reducers below
    // ...
    // Matcher added to take actions when page routing happens
    builder.addMatcher(
      (action: PayloadAction<boolean>) =>
        action.type === globalActions.setViolationsActiveTab.type ||
        action.type === detailsActions.setQueryParam.type,
      (state) => {
        state.list.totalCount = null;

        if (state.sidepanel.isSidePanelVisible) {
          state.sidepanel.isSidePanelVisible = false;
        }

        if (state.bulkaction.isFooterVisible) {
          state.bulkaction.isBulkChecked = false;
          state.bulkaction.isFooterVisible = false;
        }
      }
    );
  },
});

const { reducer: violationsTabReducer, actions: sliceActions } =
  violationsTabSlice;

export const violationsTabActions = sliceActions;

export default violationsTabReducer;
